import React, { PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import { MessageDiagnostic, useScreenMessage } from '@components/ringil3/Features/Assistant/pageContext/useScreenMessage';

interface ComponentProps {
  icon?: JSX.Element;
  buttons?: JSX.Element;
  variant: 'info' | 'warning' | 'error' | 'success' | 'alert';
  buttonsRight?: boolean;
  dataTestId?: string;
  contentClassName?: string;
  /**
   * The translation key (and params) the children were rendered from, so the AI assistant can explain
   * this exact message when the user asks about it. The component cannot recover it from the text.
   */
  diagnostic?: MessageDiagnostic;
}

const COInlineMessage: React.FC<PropsWithChildren<ComponentProps>> = ({ icon, children, variant, buttons, buttonsRight, dataTestId, contentClassName, diagnostic }) => {
  useScreenMessage(diagnostic, variant);
  return (
    <COInlineMessageInner variant={variant}>
      <GridWithIcon buttonsRight={buttonsRight}>
        {icon && <ImgWrapper>{icon}</ImgWrapper>}
        <NoteInner data-testid={dataTestId} className={contentClassName}>{children}</NoteInner>
        {!buttonsRight && buttons !== null && <div className={'text-nowrap'}>{buttons}</div>}
      </GridWithIcon>
      {buttonsRight && buttons !== null && <div className={'text-nowrap'}>{buttons}</div>}
    </COInlineMessageInner>
  );
};

const ImgWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const NoteInner = styled.div``;

const GridWithIcon = styled.div<{ buttonsRight?: boolean }>`
  display: flex;
  gap: 0.5rem;
  flex: 1;
  align-items: center;
  ${props => props.buttonsRight && css`
    justify-content: flex-start;
  `}
  p {
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    letter-spacing: 0.02em;
    /* Black */
    color: #343333;
    margin: 0;
  }
`;

const COInlineMessageInner = styled.div<Pick<ComponentProps, 'variant'>>`
  display: flex;
  border-radius: 4px;
  padding: 1rem;
  font-size: 12px;
  align-items: center;
  grow: 0;

  ${props =>
    props.variant &&
    props.variant === 'info' &&
    css`
      background-color: var(--ringil3-content-quaternary);
    `};
  ${props =>
    props.variant &&
    props.variant === 'warning' &&
    css`
      background-color: var(--ringil3-brand-light-color);
    `};
  ${props =>
    props.variant &&
    props.variant === 'error' &&
    css`
      background-color: var(--complementary-errormedium);
    `};
  ${props =>
    props.variant &&
    props.variant === 'success' &&
    css`
      background-color: var(--complementary-successmedium);
    `};
  ${props =>
      props.variant &&
      props.variant === 'alert' &&
      css`
      background-color: var(--complementary-warningmedium);
    `};
`;

export default COInlineMessage;
